import React, { FC, useEffect, useState } from "react";

import {
  Typography,
  Stack,
  Snackbar,
  Alert,
  Switch,
  Grid,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../../store";
import {
  fetchTournoi,
  fetchAllRegistrations,
  changeTournamentStatsCollection,
  generateDossards,
  regenerateAllDossards,
  clearAllDossards,
  generatePlayersCSV,
  closeRegistrationPhase,
  getTournoi,
  getAreAllDossardsGenerated,
  getIsRegistrationPhaseClosed,
  changeClosedTournamentLabel,
  changeTournamentBoardClosedLabel,
  changeTournamentBoardClosedLabelWithQueue,
  changeTournamentRegistrationStatus,
  changeTournamentAlertThresholds,
  fetchBoards,
} from "../../../features/tournoi/tournoiSlice";

import EditableField from "../../Base/EditableField";

interface AdminTournamentProps {}

const AdminTournament: FC<AdminTournamentProps> = () => {
  const tournoi = useAppSelector(getTournoi);
  const dispatch = useAppDispatch();
  const [snackBarOpened, setSnackBarOpened] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [snackBarSeverity, setSnackBarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [isDossardLoading, setIsDossardLoading] = useState(false);
  const [isCSVLoading, setIsCSVLoading] = useState(false);
  const [isClosureLoading, setIsClosureLoading] = useState(false);

  // Nouveaux sélecteurs
  const areAllDossardsGenerated = useAppSelector(getAreAllDossardsGenerated);
  const isRegistrationPhaseClosed = useAppSelector(
    getIsRegistrationPhaseClosed
  );

  console.log(
    "AdminTournament - areAllDossardsGenerated:",
    areAllDossardsGenerated
  );

  // Ajout des valeurs par défaut si non présentes
  const defaultAlertThresholds = {
    cree: { "2": 60, "3": 20 },
    enCours: { "2": 50, "3": 100 },
  };

  const alertThresholds = tournoi.alertThresholds || defaultAlertThresholds;
  const [localAlertThresholds, setLocalAlertThresholds] =
    useState(alertThresholds);

  // Charger les données nécessaires au montage du composant
  useEffect(() => {
    const loadData = async () => {
      await dispatch(fetchTournoi());
      await dispatch(fetchBoards());
      await dispatch(fetchAllRegistrations({ reload: false }));
    };
    loadData();
  }, [dispatch]);

  const handleAlertThresholdChange = (
    state: "cree" | "enCours",
    nb: "2" | "3",
    value: number
  ) => {
    setLocalAlertThresholds((prev) => ({
      ...prev,
      [state]: { ...prev[state], [nb]: value },
    }));
  };

  const handleSaveAlertThresholds = async () => {
    await dispatch(
      changeTournamentAlertThresholds({
        tournamentId: tournoi.id,
        alertThresholds: localAlertThresholds,
      })
    );
    openSnackBar("Seuils d'alerte mis à jour", "success");
  };

  const openSnackBar = (
    message: string,
    severity: "success" | "error" = "success"
  ) => {
    setSnackBarMessage(message);
    setSnackBarSeverity(severity);
    setSnackBarOpened(true);
  };

  const handleCloseSnackBar = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackBarOpened(false);
  };

  const handleDossardOperation = async (
    operation: () => any,
    successMessage: string
  ) => {
    setIsDossardLoading(true);
    try {
      const result = await dispatch(operation());

      // Vérifier si l'action a été rejetée en regardant la propriété error
      if (result.error) {
        const errorMessage =
          result.payload || "Une erreur inattendue s'est produite";
        openSnackBar(`Erreur : ${errorMessage}`, "error");
      } else {
        openSnackBar(successMessage, "success");
        // Forcer le rechargement des données pour mettre à jour le bouton CSV
        await dispatch(fetchAllRegistrations({ reload: true }));
      }
    } catch (error) {
      console.error("Erreur lors de l'opération sur les dossards:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Une erreur inattendue s'est produite";
      openSnackBar(`Erreur : ${errorMessage}`, "error");
    } finally {
      setIsDossardLoading(false);
    }
  };

  const handleGenerateCSV = async () => {
    setIsCSVLoading(true);
    try {
      const result = await dispatch(generatePlayersCSV());

      if (generatePlayersCSV.fulfilled.match(result)) {
        openSnackBar("Fichier CSV généré avec succès", "success");
      } else {
        const errorMessage =
          result.payload || "Une erreur inattendue s'est produite";
        openSnackBar(`Erreur : ${errorMessage}`, "error");
      }
    } catch (error) {
      console.error("Erreur lors de la génération du CSV:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Une erreur inattendue s'est produite";
      openSnackBar(`Erreur : ${errorMessage}`, "error");
    } finally {
      setIsCSVLoading(false);
    }
  };

  const handleCloseRegistrationPhase = async () => {
    setIsClosureLoading(true);
    try {
      const result = await dispatch(closeRegistrationPhase());

      if (closeRegistrationPhase.fulfilled.match(result)) {
        openSnackBar("Phase d'inscription clôturée avec succès", "success");
      } else {
        const errorMessage =
          result.payload || "Une erreur inattendue s'est produite";
        openSnackBar(`Erreur : ${errorMessage}`, "error");
      }
    } catch (error) {
      console.error(
        "Erreur lors de la clôture de la phase d'inscription:",
        error
      );
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Une erreur inattendue s'est produite";
      openSnackBar(`Erreur : ${errorMessage}`, "error");
    } finally {
      setIsClosureLoading(false);
    }
  };

  return (
    <>
      <Stack direction="column" spacing={3} margin={2}>
        <Stack direction="row" spacing={3}>
          <Typography>Inscriptions ouvertes :</Typography>
          <Switch
            size="small"
            checked={!tournoi?.isRegisterClosed}
            onChange={async (event) => {
              const resStatusAction = await dispatch(
                changeTournamentRegistrationStatus({
                  tournamentId: tournoi!.id,
                  isRegisterClosed: !event.target.checked,
                })
              );

              if (
                changeTournamentRegistrationStatus.fulfilled.match(
                  resStatusAction
                )
              ) {
                openSnackBar(
                  event.target.checked
                    ? `Inscriptions au tournoi ouvertes`
                    : `Inscriptions au tournoi fermées`
                );
              }
            }}
          />
        </Stack>
        <Stack direction="row" spacing={3}>
          <Typography>
            Libellé affiché lorsque les inscriptions au tournoi sont fermées :
          </Typography>
          <EditableField
            label={tournoi?.closedTournamentLabel!}
            type="string"
            onChange={async (newValue) => {
              const resAction = await dispatch(
                changeClosedTournamentLabel({
                  tournamentId: tournoi?.id,
                  closedTournamentLabel: newValue,
                })
              );

              if (changeTournamentBoardClosedLabel.fulfilled.match(resAction)) {
                openSnackBar(
                  "Libellé affiché lorsqu'un tableau est fermé mis à jour"
                );
              }
            }}
          />
        </Stack>
        <Stack direction="row" spacing={3}>
          <Typography>Libellé affiché lorsqu'un tableau est fermé :</Typography>
          <EditableField
            label={tournoi?.boardClosedDefaultLabel!}
            type="string"
            onChange={async (newValue) => {
              const resAction = await dispatch(
                changeTournamentBoardClosedLabel({
                  tournamentId: tournoi?.id,
                  boardClosedDefaultLabel: newValue,
                })
              );

              if (changeTournamentBoardClosedLabel.fulfilled.match(resAction)) {
                openSnackBar(
                  "Libellé affiché lorsqu'un tableau est fermé mis à jour"
                );
              }
            }}
          />
        </Stack>
        <Stack direction="row" spacing={3}>
          <Typography>
            Libellé affiché lorsqu'il y a une file d'attente :
          </Typography>
          <EditableField
            label={tournoi?.boardClosedWithQueueLabel!}
            type="string"
            onChange={async (newValue) => {
              const resAction = await dispatch(
                changeTournamentBoardClosedLabelWithQueue({
                  tournamentId: tournoi?.id,
                  boardClosedWithQueueLabel: newValue,
                })
              );

              if (
                changeTournamentBoardClosedLabelWithQueue.fulfilled.match(
                  resAction
                )
              ) {
                openSnackBar(
                  "Libellé affiché lorsqu'un tableau est fermé et qu'il a une file d'attente mis à jour "
                );
              }
            }}
          />
        </Stack>
        <Stack direction="row" spacing={3}>
          <Typography>Collection Firestore pour les statistiques :</Typography>
          <EditableField
            label={tournoi?.statsCollectionName || "matchs"}
            type="string"
            onChange={async (newValue) => {
              const resAction = await dispatch(
                changeTournamentStatsCollection({
                  tournamentId: tournoi?.id,
                  statsCollectionName: newValue || "matchs",
                })
              );

              if (changeTournamentStatsCollection.fulfilled.match(resAction)) {
                openSnackBar("Collection des statistiques mise à jour");
              }
            }}
          />
        </Stack>
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Typography variant="h6">
            Seuils d'alerte pour les matchs en attente
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">
                Matchs non lancés (CREE, DOUBLON, LIBERE)
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Typography>2 joueurs :</Typography>
                <input
                  type="number"
                  min={1}
                  value={localAlertThresholds.cree["2"]}
                  onChange={(e) =>
                    handleAlertThresholdChange(
                      "cree",
                      "2",
                      Number(e.target.value)
                    )
                  }
                  style={{ width: 60 }}
                />
                <Typography>min</Typography>
              </Box>
              <Box display="flex" gap={2} alignItems="center" mt={1}>
                <Typography>3 joueurs :</Typography>
                <input
                  type="number"
                  min={1}
                  value={localAlertThresholds.cree["3"]}
                  onChange={(e) =>
                    handleAlertThresholdChange(
                      "cree",
                      "3",
                      Number(e.target.value)
                    )
                  }
                  style={{ width: 60 }}
                />
                <Typography>min</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">
                Matchs en cours (ENCOURS)
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Typography>2 joueurs :</Typography>
                <input
                  type="number"
                  min={1}
                  value={localAlertThresholds.enCours["2"]}
                  onChange={(e) =>
                    handleAlertThresholdChange(
                      "enCours",
                      "2",
                      Number(e.target.value)
                    )
                  }
                  style={{ width: 60 }}
                />
                <Typography>min</Typography>
              </Box>
              <Box display="flex" gap={2} alignItems="center" mt={1}>
                <Typography>3 joueurs :</Typography>
                <input
                  type="number"
                  min={1}
                  value={localAlertThresholds.enCours["3"]}
                  onChange={(e) =>
                    handleAlertThresholdChange(
                      "enCours",
                      "3",
                      Number(e.target.value)
                    )
                  }
                  style={{ width: 60 }}
                />
                <Typography>min</Typography>
              </Box>
            </Grid>
          </Grid>
          <Button
            variant="contained"
            onClick={handleSaveAlertThresholds}
            sx={{ mt: 2, alignSelf: "flex-start" }}
          >
            Enregistrer les seuils d'alerte
          </Button>
        </Stack>

        <Stack spacing={2} sx={{ mt: 4 }}>
          <Typography variant="h6">Gestion des dossards</Typography>
          <Typography variant="body2" color="text.secondary">
            Les dossards sont attribués par ordre décroissant du nombre de
            points, puis par ordre alphabétique du nom et du prénom.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                await handleDossardOperation(
                  generateDossards,
                  "Dossards générés avec succès"
                );
              }}
              disabled={isDossardLoading}
              startIcon={
                isDossardLoading ? <CircularProgress size={16} /> : null
              }
            >
              {isDossardLoading
                ? "Génération..."
                : "Générer dossards manquants"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={async () => {
                await handleDossardOperation(
                  regenerateAllDossards,
                  "Tous les dossards ont été régénérés"
                );
              }}
              disabled={isDossardLoading}
              startIcon={
                isDossardLoading ? <CircularProgress size={16} /> : null
              }
            >
              {isDossardLoading
                ? "Régénération..."
                : "Régénérer tous les dossards"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={async () => {
                await handleDossardOperation(
                  clearAllDossards,
                  "Tous les dossards ont été supprimés"
                );
              }}
              disabled={isDossardLoading}
              startIcon={
                isDossardLoading ? <CircularProgress size={16} /> : null
              }
            >
              {isDossardLoading
                ? "Suppression..."
                : "Supprimer tous les dossards"}
            </Button>
          </Stack>
        </Stack>

        <Stack spacing={2} sx={{ mt: 4 }}>
          <Typography variant="h6">
            Export et gestion des inscriptions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Générez un fichier CSV des joueurs inscrits et gérez la phase
            d'inscription.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateCSV}
              disabled={!areAllDossardsGenerated || isCSVLoading}
              startIcon={isCSVLoading ? <CircularProgress size={16} /> : null}
            >
              {isCSVLoading
                ? "Génération..."
                : "Générer CSV des joueurs inscrits"}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleCloseRegistrationPhase}
              disabled={isRegistrationPhaseClosed || isClosureLoading}
              startIcon={
                isClosureLoading ? <CircularProgress size={16} /> : null
              }
            >
              {isClosureLoading
                ? "Clôture..."
                : isRegistrationPhaseClosed
                ? "Phase d'inscription déjà clôturée"
                : "Clôturer la phase d'inscription"}
            </Button>
          </Stack>
          {!areAllDossardsGenerated && (
            <Typography variant="body2" color="warning.main">
              ⚠️ Les dossards doivent être générés avant de pouvoir exporter le
              CSV.
            </Typography>
          )}
          {isRegistrationPhaseClosed && (
            <Typography variant="body2" color="info.main">
              ℹ️ La phase d'inscription est clôturée. Les nouvelles inscriptions
              seront marquées comme "post-clôture".
            </Typography>
          )}
        </Stack>
      </Stack>

      <Snackbar
        open={snackBarOpened}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        onClose={handleCloseSnackBar}
      >
        <Alert
          onClose={handleCloseSnackBar}
          severity={snackBarSeverity}
          sx={{ width: "100%" }}
        >
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminTournament;
